/**
 * Responsibility: Implement atlas knowledge behavior for the ui/helpers surface.
 * Scope: Owns ui/helpers concerns only; cross-surface orchestration is excluded.
 */
import { parseLocKey } from '../../engine/state'
import type { Atlas, NarratorAIResponse } from "@anyquest/shared-core"

type AtlasHints = NonNullable<NarratorAIResponse['atlasHints']>
type AtlasHintPlace = NonNullable<AtlasHints['places']>[number]
type AtlasHintPath = NonNullable<AtlasHints['paths']>[number]

export interface AtlasKnowledgeApplyResult {
  placesAdded: number
  placesUpdated: number
  pathsAdded: number
}

function normalizeLocKey(raw: unknown): string | null {
  const text = String(raw ?? '').trim()
  if (!/^-?\d+_-?\d+$/.test(text)) return null
  const parsed = parseLocKey(text)
  if (!parsed) return null
  const [x, y] = parsed
  if (!Number.isInteger(x) || !Number.isInteger(y)) return null
  return `${x}_${y}`
}

function isNeighbor(aKey: string, bKey: string): boolean {
  const a = parseLocKey(aKey)
  const b = parseLocKey(bKey)
  if (!a || !b) return false
  const manhattan = Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1])
  return manhattan === 1
}

function hasEdge(atlas: Atlas, from: string, to: string): boolean {
  const a = parseLocKey(from)
  const b = parseLocKey(to)
  if (!a || !b) return false
  return atlas.edges.some(edge =>
    (edge.x1 === a[0] && edge.y1 === a[1] && edge.x2 === b[0] && edge.y2 === b[1])
    || (edge.x1 === b[0] && edge.y1 === b[1] && edge.x2 === a[0] && edge.y2 === a[1]),
  )
}

function ensureNode(
  atlas: Atlas,
  key: string,
  place?: AtlasHintPlace,
): { added: boolean; updated: boolean } {
  const fallbackName = `Unknown Site ${key}`
  const nextName = String(place?.name ?? '').trim() || fallbackName
  const nextDesc = String(place?.desc ?? '').trim()

  if (!atlas.nodes[key]) {
    atlas.nodes[key] = {
      name: nextName,
      desc: nextDesc || undefined,
      items: [],
      dormantFlags: [],
    }
    return { added: true, updated: false }
  }

  const node = atlas.nodes[key]
  let updated = false
  if (place?.name && (!node.name || /^Unknown Site\b/i.test(node.name))) {
    node.name = nextName
    updated = true
  }
  if (nextDesc && !String(node.desc ?? '').trim()) {
    node.desc = nextDesc
    updated = true
  }
  return { added: false, updated }
}

export function applyAtlasKnowledge(
  atlas: Atlas,
  hints: NarratorAIResponse['atlasHints'] | undefined,
): AtlasKnowledgeApplyResult {
  const placesRaw = Array.isArray(hints?.places) ? hints?.places : []
  const pathsRaw = Array.isArray(hints?.paths) ? hints?.paths : []

  let placesAdded = 0
  let placesUpdated = 0
  let pathsAdded = 0

  const placeLimit = placesRaw.slice(0, 12)
  for (const place of placeLimit) {
    const key = normalizeLocKey(place?.key)
    if (!key) continue
    const change = ensureNode(atlas, key, place)
    if (change.added) placesAdded += 1
    if (change.updated) placesUpdated += 1
  }

  const pathLimit = pathsRaw.slice(0, 24)
  for (const path of pathLimit) {
    const from = normalizeLocKey((path as AtlasHintPath)?.from)
    const to = normalizeLocKey((path as AtlasHintPath)?.to)
    if (!from || !to || from === to) continue
    if (!isNeighbor(from, to)) continue

    const fromChange = ensureNode(atlas, from)
    const toChange = ensureNode(atlas, to)
    if (fromChange.added) placesAdded += 1
    if (fromChange.updated) placesUpdated += 1
    if (toChange.added) placesAdded += 1
    if (toChange.updated) placesUpdated += 1

    if (hasEdge(atlas, from, to)) continue
    const [x1, y1, z1] = parseLocKey(from) as [number, number, number]
    const [x2, y2, z2] = parseLocKey(to) as [number, number, number]
    const risk = typeof path.risk === 'number' ? Math.max(0, Math.min(1, path.risk)) : undefined
    atlas.edges.push({ x1, y1, z1, x2, y2, z2, risk })
    pathsAdded += 1
  }

  return { placesAdded, placesUpdated, pathsAdded }
}
